import classNames from 'classnames';
import { motion } from 'motion/react';
import React, { useRef, useState } from 'react';

import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	ContentWithTooltip,
	Group,
	LabelPrice,
	Text
} from '@ekhodealer/ekho-common/rev';

import type {
	TextProps} from '@ekhodealer/ekho-common/rev';

import './LineItem.scss';

/** Set automatically when used as subItem. Root and direct children look the same; only grandchildren use the nested style. */
export type LineItemPresentation = 'default' | 'nested';

export interface LineItemProps {
	title: React.ReactNode | string;
	titleTextProps?: Omit<TextProps, 'children'>;
	price: React.ReactNode | number;
	priceTextProps?: Omit<TextProps, 'children'>;
	isToggleable?: boolean;
	isTotal?: boolean;
	presentation?: LineItemPresentation;
	subItems?: React.ReactElement<LineItemProps>[];
}

/** Presentation passed to our children: root → default, direct child or nested → nested (so only grandchildren get nested style). */
function getChildPresentation(current: LineItemPresentation | undefined): LineItemPresentation {
	return current === undefined ? 'default' : 'nested';
}

export function LineItem({
	title,
	titleTextProps,
	price,
	priceTextProps,
	isToggleable,
	isTotal,
	presentation,
	subItems,
}: LineItemProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const priceSpanRef = useRef<HTMLSpanElement>(null);
	const hasChildren = !!subItems?.length;

	const selectPriceOnClick = () => {
		const node = priceSpanRef.current;
		if (!node) return;
		const selection = window.getSelection();
		if (!selection) return;
		const range = document.createRange();
		range.selectNodeContents(node);
		selection.removeAllRanges();
		selection.addRange(range);
	};
	const showNestedStyle = presentation === 'nested' && !hasChildren;

	const subItemsWithPresentation = subItems?.map((child, index) =>
		React.cloneElement(child, {
			...child.props,
			presentation: getChildPresentation(presentation),
			key: child.key ?? index,
		}),
	);

	const titleIsString = typeof title === 'string';
	const priceIsNumber = typeof price === 'number';

	if (titleIsString) {
		title = (
			<Text
				size='bodySmall'
				weight={isTotal ? 'semibold' : 'regular'}
				align='left'
				// eslint-disable-next-line react/jsx-props-no-spreading -- configurable Text props forwarded from parent
				{...titleTextProps}
			>
				{title}
			</Text>
		);
	}

	if (priceIsNumber) {
		const priceValue = price as number;
		let formattedPrice;
		if (priceValue === 0) {
			formattedPrice = "Free";
		} else if (priceValue > 0) {
			formattedPrice = <LabelPrice format='dollarsAndCents' price={priceValue} />;
		} else if (priceValue < 0) {
			formattedPrice = <span>(<LabelPrice format='dollarsAndCents' price={Math.abs(priceValue)} />)</span>;
		}
		price = (
			<span
				ref={priceSpanRef}
				onClick={selectPriceOnClick}
			>
				<Text
					size='bodySmall'
					weight={isTotal ? 'semibold' : 'regular'}
					// eslint-disable-next-line react/jsx-props-no-spreading -- configurable Text props forwarded from parent
					{...priceTextProps}
				>
					{formattedPrice}
				</Text>
			</span>
		);
	}

	if (!isToggleable || !hasChildren) {
		return (
			<div data-component='line-item' className={
				classNames({
					'line-item--nested': showNestedStyle,
					'line-item--has-children': hasChildren,
					'line-item-no-children': !hasChildren,
					'line-item-is-not-toggleable': !isToggleable,
				})
			}>
				<div className='line-item__left'>{title}</div>
				<div className='line-item__right'>{price}</div>
				{hasChildren && <div className='line-item-children'>{subItemsWithPresentation}</div>}
			</div>
		);
	}

	return (
		<div data-component='line-item'>
			<Accordion
				className='line-item-expandable-section'
				isExpanded={isExpanded}
				onExpandedChange={setIsExpanded}>
				<AccordionHeader>
					<Group className='line-item-header-group' itemsAlignY='center' noWrap>
						<ContentWithTooltip
							content={
								<div
									className='line-item-faux-button'
									data-rev-component='button'
									data-rev-variant='secondary'
									data-rev-size='tiny'
									data-rev-rounding='full'
									onClick={() => setIsExpanded((v) => !v)}
									role='button'
									aria-label={isExpanded ? 'Collapse' : 'Expand'}
								>
									<PlusIcon isExpanded={isExpanded} />
								</div>
							}
							tooltipContent={isExpanded ? 'Collapse' : 'Expand'}
							tooltipProps={{ placement: 'top', className: 'line-item-faux-button-tooltip' }}
						/>
						{title}
					</Group>
				</AccordionHeader>
				{price}
				<AccordionBody>
					<div className='line-item-children'>{subItemsWithPresentation}</div>
				</AccordionBody>
			</Accordion>
		</div>
	);
}

/* -------------------------------------------- * Icons * ------------------------------------------ */
function PlusIcon({ className, isExpanded = false }: { className?: string; isExpanded?: boolean }) {
	return (
		<svg className={className} xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'>
			<g style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
				<motion.path
					className='vertical-line'
					d='M 7.167 3.5 C 7.443 3.501 7.667 3.724 7.667 4 C 7.667 4.276 7.443 4.5 7.167 4.5 L 0.5 4.5 C 0.224 4.5 0 4.276 0 4 C 0 3.724 0.224 3.5 0.5 3.5 L 7.167 3.5 Z'
					style={{ transformBox: 'fill-box', transformOrigin: '50% 50%' }}
					animate={{ scaleX: isExpanded ? 0 : 1, opacity: isExpanded ? 0 : 1 }}
					transition={{ duration: 0.15 }}
				/>
			</g>
			<path className="horizontal-line" style={{ fill: 'currentColor' }} d="M 7.32 3.5 C 7.596 3.5 7.82 3.724 7.82 4 C 7.82 4.275 7.596 4.499 7.32 4.5 L 0.653 4.5 C 0.377 4.5 0.153 4.276 0.153 4 C 0.153 3.723 0.377 3.5 0.653 3.5 L 7.32 3.5 Z"></path>
		</svg >
	);
}

